import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { useCurrentUser } from '../../session.js';
import type { AuditEntry } from './types.js';

/** Rotulos das entidades que a API grava (ver `recordAudit` nas rotas). */
const ENTIDADES: Record<string, string> = {
  task: 'Tarefa',
  list: 'Lista',
  tag: 'Tag',
  habit: 'Hábito',
  habit_log: 'Registro de hábito',
  countdown: 'Contagem',
  pomodoro_session: 'Foco',
  push_subscription: 'Notificações',
  user_settings: 'Ajustes',
  export: 'Exportação',
  import: 'Importação',
  sync: 'Sincronização',
};

const ACOES: Record<string, string> = {
  create: 'criou',
  update: 'alterou',
  delete: 'excluiu',
  restore: 'restaurou',
  complete: 'concluiu',
  login: 'entrou',
  export: 'exportou',
  import: 'importou',
};

/**
 * Resumo de uma linha do diff. A trilha guarda o objeto inteiro, mas a lista
 * so precisa dizer O QUE mudou: os nomes dos campos, e o valor quando ele e
 * curto o bastante para caber (titulo, nome).
 */
export function resumoDoDiff(diff: unknown): string {
  if (diff === null || diff === undefined) return '';
  if (typeof diff !== 'object') return String(diff).slice(0, 80);

  const partes = Object.entries(diff as Record<string, unknown>).map(([campo, valor]) =>
    typeof valor === 'string' && valor.length <= 40
      ? `${campo}: ${valor}`
      : typeof valor === 'number' || typeof valor === 'boolean'
        ? `${campo}: ${String(valor)}`
        : campo,
  );
  const visiveis = partes.slice(0, 3).join(', ');
  return partes.length > 3 ? `${visiveis} +${partes.length - 3}` : visiveis;
}

/**
 * Aba "Auditoria" da tela Mais (spec 6.11).
 *
 * A trilha ja era gravada em toda escrita desde a fase 1, mas so dava para le-la
 * direto no D1. Esta tela fecha o circuito para o proprio usuario; o admin
 * ganha a opcao de ver a de todos (a API recusa isso para membro).
 */
export function AuditList() {
  const user = useCurrentUser();
  const [entity, setEntity] = useState('');
  const [todos, setTodos] = useState(false);
  const ehAdmin = user.role === 'admin';

  const consulta = useInfiniteQuery({
    queryKey: ['audit', { entity, todos }],
    queryFn: ({ pageParam }) =>
      api.audit({
        ...(pageParam !== undefined ? { before: pageParam } : {}),
        ...(entity ? { entity } : {}),
        all: todos,
      }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (ultima) => ultima.nextBefore ?? undefined,
  });

  // Hora no fuso do USUARIO, nao no do navegador: quem viaja continua lendo
  // a trilha no mesmo relogio do resto do app.
  const formato = new Intl.DateTimeFormat('pt-BR', {
    timeZone: user.timezone,
    dateStyle: 'short',
    timeStyle: 'short',
  });

  const linhas: AuditEntry[] = consulta.data?.pages.flatMap((p) => p.entries) ?? [];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-text-mut">
          Entidade
          <select
            value={entity}
            onChange={(e) => setEntity(e.target.value)}
            className="min-h-touch rounded-sm bg-surface-2 px-2 text-sm text-text"
          >
            <option value="">Todas</option>
            {Object.entries(ENTIDADES).map(([id, rotulo]) => (
              <option key={id} value={id}>
                {rotulo}
              </option>
            ))}
          </select>
        </label>

        {ehAdmin ? (
          <label className="flex items-center gap-2 text-sm text-text-mut">
            <input
              type="checkbox"
              checked={todos}
              onChange={(e) => setTodos(e.target.checked)}
            />
            Todos os usuários
          </label>
        ) : null}
      </div>

      {consulta.isPending ? (
        <p className="p-4 text-sm text-text-mut">Carregando auditoria…</p>
      ) : consulta.isError ? (
        <p className="rounded bg-surface p-4 text-center text-sm text-text-mut shadow-card">
          Não foi possível carregar a auditoria.
        </p>
      ) : linhas.length === 0 ? (
        <p className="rounded bg-surface p-4 text-center text-sm text-text-mut shadow-card">
          Nenhum registro de auditoria.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {linhas.map((linha) => {
            const resumo = resumoDoDiff(linha.diff);
            return (
              <li key={linha.id} className="rounded bg-surface p-3 shadow-card">
                <p className="text-sm">
                  <span className="font-medium">{ENTIDADES[linha.entity] ?? linha.entity}</span>{' '}
                  <span className="text-text-mut">· {ACOES[linha.action] ?? linha.action}</span>
                </p>
                {/* break-words: um diff com texto sem espaco nao pode estourar 390px. */}
                {resumo ? (
                  <p className="break-words text-sm text-text-mut">{resumo}</p>
                ) : null}
                <p className="text-xs text-text-mut">
                  <time dateTime={new Date(linha.at).toISOString()}>
                    {formato.format(linha.at)}
                  </time>
                </p>
              </li>
            );
          })}
        </ul>
      )}

      {consulta.hasNextPage ? (
        <button
          type="button"
          onClick={() => void consulta.fetchNextPage()}
          disabled={consulta.isFetchingNextPage}
          className="min-h-touch self-center rounded-sm bg-surface-2 px-4 text-sm font-medium"
        >
          {consulta.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
        </button>
      ) : null}
    </div>
  );
}
